import fetch from 'cross-fetch';

import { Asset, ImageAsset, PrecompositionAsset } from '../assets';
import { AssetType, LayerType, PropertyType } from '../constants';
import { Character, FontList } from '../fonts';
import { GroupLayer, ImageLayer, Layer, PrecompositionLayer, ShapeLayer, SolidLayer, TextLayer } from '../layers';
import { Marker } from '../markers';
import { Property } from '../properties';
import { GroupShape } from '../shapes/group-shape';
import { Shape } from '../shapes/shape';
import { KeyFrame } from '../timeline';
import { rgbaToHex } from '../utils/color-spaces';
import { useRegistry } from '../utils/use-registry';
import { TextDocument } from '../values';
import { Color } from '../values/color';
import { Meta } from './meta';

interface parentLocatorInterface {
  [key: string]: string;
}

/**
 * Animation contains all the information about the Lottie animation.
 */
export class Animation {
  public assets: Asset[] = [];
  public frameRate = 60;
  public height = 0;
  public inPoint = 0;
  public is3D = false;
  public layers: Layer[] = [];
  public markers: Marker[] = [];
  public meta: Meta = new Meta(this);
  public name = '';
  public outPoint = 0;
  public version = ``;
  public width = 0;
  public fonts: FontList = new FontList();
  public characters: Character[] = [];

  /**
   * Create a class instance from the URL to the Lottie JSON.
   *
   * @param url     URL string
   * @returns       Animation instance
   */
  public static async fromURL(url: string): Promise<Animation> {
    if (typeof url !== 'string') {
      throw new Error(`The url value must be a string`);
    }

    let json;

    try {
      // Try construct an absolute URL from the src URL
      const srcUrl: URL = new URL(url);

      // Fetch the JSON file from the URL
      const result = await fetch(srcUrl.toString());
      json = await result.json();
    } catch (err) {
      throw new Error(`An error occurred while trying to load the Lottie file from URL`);
    }

    // Create a new animation instance and import JSON
    const anim = new Animation();

    return anim.fromJSON(json);
  }

  /**
   * Returns whether the given object looks like a valid Lottie JSON structure.
   *
   * This method checks for the presense of the mandatory fields 'v', 'ip',
   * 'op', 'layers', 'fr', 'w' and 'h' in the object.
   *
   * @param json    Object
   * @returns       Boolean true if it is a valid Lottie
   */
  public static isLottie(json: Record<string, any>): boolean {
    const mandatory = ['v', 'ip', 'op', 'layers', 'fr', 'w', 'h'];

    return mandatory.every(field => Object.prototype.hasOwnProperty.call(json, field));
  }

  /*
   * Returns all the colors used in the animation.
   *
   * @returns Array of colors.
   */
  public get colors(): Color[] {
    const colors: Set<string> = new Set();

    [...useRegistry().keys()]
      // Filter color properties
      .filter((p: Property) => p.type === PropertyType.COLOR)
      .forEach((cp: Property) => {
        cp.values.forEach((v: KeyFrame) => {
          colors.add(JSON.stringify(v.value));
        });
      });

    return Array.from(colors).map(c => Color.fromJSON(JSON.parse(c)));
  }

  /*
   * Returns all the colors used in the animation.
   *
   * @returns Array of colors.
   */

  public get colorsVerbose(): Record<string, any> {
    const colors: parentLocatorInterface = {};

    [...useRegistry().keys()]
      // Filter color properties
      .filter((p: Property) => p.type === PropertyType.COLOR)
      .forEach((cp: Property, index: number) => {
        const parent = cp.getParent();
        const pathString = this.parentPath(parent);

        const pathCopy = pathString.slice();

        cp.values.forEach((v: KeyFrame) => {
          pathCopy.unshift('Frame ' + v.frame);
          pathCopy.unshift(index.toString());

          const colorParts = v.value as [number, number, number, number];

          colors[pathCopy.join('.')] = rgbaToHex([
            Math.round(colorParts[0] * 255),
            Math.round(colorParts[1] * 255),
            Math.round(colorParts[2] * 255),
            colorParts[3],
          ]);
        });
      });

    return colors;
  }

  /*
   * return all the text layers with layer name
   */

  public get textLayers(): Record<string, any> {
    const textLayers: parentLocatorInterface = {};
    const layers = this.getLayersByType(LayerType.TEXT) as TextLayer[];
    layers.forEach((layer, index) => {
      const textDocument = layer.textData?.textDocument.values[0];
      let layerText = '';
      if (textDocument !== undefined) {
        layerText = (textDocument.value as TextDocument).text;
      }
      textLayers[index + '.' + layer.name] = layerText;
    });
    return textLayers;
  }

  /*
   * returns the names of all prents given a shape
   */

  public parentPath(shape: any, paths: string[] = []): string[] {
    if (shape.parent === undefined) {
      // stop recursion
      paths.push(shape.name);
      return paths;
    }
    paths.push(shape.name);
    return this.parentPath(shape.parent, paths);
  }

  /**
   * Returns the running time of the animation in seconds.
   *
   * @returns Number of seconds.
   */
  public get duration(): number {
    return this.totalFrames / this.frameRate;
  }

  /**
   * Returns the size of the Lottie JSON in bytes.
   *
   * @returns Number of bytes.
   */
  public get fileSize(): number {
    return new TextEncoder().encode(JSON.stringify(this)).length;
  }

  /**
   * Returns the total number of frames in the animation.
   *
   * @returns Number of frames.
   */
  public get totalFrames(): number {
    return this.outPoint - this.inPoint;
  }

  /**
   * Creates and returns a new asset of the given type.
   *
   * @param type    Asset type string.
   */
  public createAsset(type: AssetType): Asset {
    if (type === AssetType.PRECOMPOSITION) {
      return new PrecompositionAsset(this);
    } else if (type === AssetType.IMAGE) {
      return new ImageAsset(this);
    }

    throw new Error(`Invalid or unknown asset type ${type}`);
  }

  /**
   * Creates and returns a new asset from JSON.
   *
   * @param json    JSON object.
   */
  public createAssetFromJSON(json: Record<string, any>): Asset {
    try {
      const asset = this.createAsset('layers' in json ? AssetType.PRECOMPOSITION : AssetType.IMAGE);

      return asset.fromJSON(json);
    } catch {
      throw new Error(`Unable to create asset from JSON`);
    }
  }

  /**
   * Creates and returns a new layer of the given type.
   *
   * @param type    Layer type string.
   */
  public createLayer(type: LayerType): Layer {
    if (type === LayerType.PRECOMPOSITION) {
      return new PrecompositionLayer(this);
    } else if (type === LayerType.SHAPE) {
      return new ShapeLayer(this);
    } else if (type === LayerType.GROUP) {
      return new GroupLayer(this);
    } else if (type === LayerType.SOLID) {
      return new SolidLayer(this);
    } else if (type === LayerType.IMAGE) {
      return new ImageLayer(this);
    } else if (type === LayerType.TEXT) {
      return new TextLayer(this);
    }

    throw new Error(`Invalid or unknown layer type: ${type}`);
  }

  /**
   * Creates and returns a new layer from JSON.
   *
   * @param json    JSON object.
   */
  public createLayerFromJSON(json: Record<string, any>): Layer {
    try {
      const layer = this.createLayer(json.ty);

      return layer.fromJSON(json);
    } catch (e) {
      console.log(e);
      throw new Error(`Unable to create layer type from JSON: ${json.ty}`);
    }
  }

  /**
   * Creates layers from json layers array
   *
   * @param jsonLayers    array containing JSON objects representing layers
   */
  public createLayersFromJSONArray(jsonLayers: Record<string, any>[]): Layer[] {
    const layerRegistry = new Map<number, Layer>();
    const layerToParent: [Layer, number][] = [];
    const layers: Layer[] = [];

    jsonLayers.forEach((jLayer: Record<string, any>) => {
      const layer = this.createLayerFromJSON(jLayer);
      if (layer) {
        if (jLayer.parent !== undefined) {
          layerToParent.push([layer, jLayer.parent]);
        }
        if (layer.index !== undefined) {
          layerRegistry.set(layer.index, layer);
        }
        layers.push(layer);
      }
    });

    layerToParent.forEach(([layer, parentId]) => {
      layer.parent = layerRegistry.get(parentId);
    });

    return layers;
  }

  /**
   * Creates and returns a new marker.
   */
  public createMarker(): Marker {
    return new Marker();
  }

  /**
   * Creates and returns a new marker from JSON.
   *
   * @param json    JSON object.
   */
  public createMarkerFromJSON(json: Record<string, any>): Marker {
    try {
      const marker = this.createMarker();

      return marker.fromJSON(json);
    } catch (e) {
      console.log(e);
      throw new Error(`Unable to create marker from JSON`);
    }
  }

  /*
   * Convert the Lottie JSON object to class instance.
   *
   * @param json    JSON object
   * @returns       Animation instance
   */
  public fromJSON(json: Record<string, any>): Animation {
    if (Animation.isLottie(json) === false) {
      throw new Error(`The given object is not a valid Lottie JSON structure`);
    }

    this.frameRate = json.fr;
    this.height = json.h;
    this.inPoint = json.ip;
    this.is3D = json.ddd;
    this.name = json.nm;
    this.outPoint = json.op;
    this.version = json.v;
    this.width = json.w;

    if ('assets' in json) {
      this.assets = json.assets.map((jAsset: Record<string, any>) => this.createAssetFromJSON(jAsset)).filter(Boolean);
    }

    this.layers = this.createLayersFromJSONArray(json.layers);

    if ('markers' in json) {
      this.markers = json.markers
        .map((jMarker: Record<string, any>) => this.createMarkerFromJSON(jMarker))
        .filter(Boolean);
    }

    if ('meta' in json) {
      this.meta.fromJSON(json.meta);
    }

    if ('fonts' in json) {
      this.fonts.fromJSON(json.fonts);
    }

    if ('chars' in json) {
      this.characters = json.chars.map((charJSON: Record<string, any>) => new Character().fromJSON(charJSON));
    }

    return this;
  }

  /**
   * Returns the layer with the given ID.
   *
   * @param id    Layer ID string.
   * @returns     Layer instance.
   */
  public getLayerById(id: string): Layer | undefined {
    // Validate argument type
    if (typeof id !== 'string') {
      throw new Error(`ID value must be a string`);
    }

    return this.layers.find((layer: Layer) => layer.id === id);
  }

  public getShapeById(id: string): Shape | null {
    let queried_shape = null;
    // Validate argument type
    if (typeof id !== 'string') {
      throw new Error(`ID value must be a string`);
    }
    this.layers.forEach(layer => {
      if (layer instanceof ShapeLayer) {
        layer.shapes.forEach(shape => {
          if (shape.id === id) {
            queried_shape = shape;
            return;
          }
        });
      }
    });
    return queried_shape;
  }

  public getShapesByClassName(className: string): Shape[] {
    const shapes: Shape[] = [];
    // Validate argument type
    if (typeof className !== 'string') {
      throw new Error(`ID value must be a string`);
    }
    this.layers.forEach(layer => {
      if (layer instanceof ShapeLayer) {
        layer.shapes.forEach(shape => {
          if (shape.classNames?.includes(className)) {
            shapes.push(shape);
          }
          if (shape instanceof GroupShape) {
            // group shapes will have a shapes array that contains all of the shapes that are grouped in
            const groupedShapes = shape.shapes;
            groupedShapes.forEach(groupedShape => {
              if (groupedShape.classNames?.includes(className)) {
                shapes.push(shape);
              }
            });
          }
        });
      }
    });
    return shapes;
  }

  /**
   * Returns the layers with the given class.
   *
   * @param className    Layer class name string.
   * @returns            Array of layer instances.
   */
  public getLayersByClassName(className: string): Layer[] {
    // Validate argument type
    if (typeof className !== 'string') {
      throw new Error(`Class name value must be a string`);
    }

    return this.layers.filter((layer: Layer) => layer.classNames.includes(className));
  }

  /**
   * Returns the layers of the given type.
   *
   * @param type    LayerType value.
   * @returns       Layer instance.
   */
  public getLayersByType(type: LayerType): Layer[] {
    // Validate argument type
    if (type in LayerType === false) {
      throw new Error(`Type value must be a valid LayerType value`);
    }

    return this.layers.filter((layer: Layer) => layer.type === type);
  }

  /**
   * Convert the class instance to Lottie JSON object.
   *
   * Called by Javascript when serializing object with JSON.stringify()
   *
   * @returns       JSON object
   */
  public toJSON(key?: string): Record<string, any> | undefined {
    if (key) {
      return undefined;
    }
    const chars = this.characters.map(char => char.toJSON());

    return {
      ...(this.assets.length > 0 && { assets: this.assets }),
      ddd: this.is3D ? 1 : 0,
      fr: this.frameRate,
      h: this.height,
      ip: this.inPoint,
      layers: this.layers.map(layer => layer.toJSON()),
      ...(this.markers.length > 0 && { markers: this.markers.map(marker => marker.toJSON()) }),
      meta: this.meta,
      nm: this.name,
      op: this.outPoint,
      v: this.version || '5.6.0',
      w: this.width,
      ...(this.fonts.list.length > 0 && { fonts: this.fonts.toJSON() }),
      chars: chars.length > 0 ? chars : undefined,
    };
  }
}
